/**
 * Management response to an internal audit draft report.
 * Route: /audit/respond/:token
 *
 * The link in the report email is the credential (same model as the
 * Workstream respond page): no PSI login. Nothing is fetched until the
 * person clicks Continue, so a mail scanner never consumes the link.
 *
 * The page reads the way a department reads it (operator order
 * 2026-10-19): pick your area (Organization determinations for UM,
 * Reconsiderations for Appeals, Grievances, Payment) or all of them, see
 * every finding in that area in one table, then open each finding to
 * respond. One card per finding: a deficient listing (Provider Directory
 * Accuracy) or a CONDITION of a protocol program (one requirement not met,
 * with the sample cases under it, the way CMS writes it). What the audit
 * found, then the response (agree, partially agree, disagree with the
 * basis), the planned correction, its owner and its date, and evidence of
 * remediation. Plain language, nothing to learn.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';
const fmt = (d) => (d ? new Date(String(d).length === 10 ? `${d}T00:00:00Z` : d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }) : '');
const PROGRAM_NAMES = { pda: 'Provider Directory Accuracy', odag: 'ODAG', cdag: 'CDAG', snpcc: 'SNPCC', website: 'Website' };
const ALL = 'all';

// The area a finding is filed under when every area shows at once: its
// first (protocol order). A finding in two areas is listed under both when
// one of them is selected.
const primaryArea = (l) => (Array.isArray(l.areas) && l.areas.length ? l.areas[0] : 'other');
const inArea = (l, key) => key === ALL || (Array.isArray(l.areas) ? l.areas.includes(key) : key === 'other');
const answered = (l) => !!(l.response && l.response.position);

const storageKey = (token) => `audit-respond-area:${String(token || '').slice(0, 16)}`;
const readChoice = (token) => { try { return window.localStorage.getItem(storageKey(token)) || null; } catch { return null; } };
const keepChoice = (token, v) => { try { window.localStorage.setItem(storageKey(token), v); } catch { /* per-viewer convenience only */ } };
// On a shared link (one draft report email to every addressee), who the
// person said they are; remembered on this browser only.
const asKey = (token) => `audit-respond-as:${String(token || '').slice(0, 16)}`;
const readAs = (token) => { try { return window.localStorage.getItem(asKey(token)) || ''; } catch { return ''; } };
const keepAs = (token, v) => { try { window.localStorage.setItem(asKey(token), v); } catch { /* per-viewer convenience only */ } };

// initialData and initialArea exist for the render harness only (a static
// render of the page with sample findings); the live page always starts
// at the Continue gate and fetches.
//
// Embedded (operator order 2026-10-27): the same page inside the Audit app
// for a signed in department user named on the audit. `base` is the session
// API for that audit (/api/audit/my-audits/internal/:id) with the same
// sub-paths as the link API; there is no Continue gate, the session is the
// credential, and the engagement notice shows above the findings.
export default function AuditResponsePage({ initialData = null, initialArea = null, base = null, embedded = false, storageId = null }) {
  const { token: routeToken } = useParams();
  const token = routeToken || storageId || 'session';
  const api = base || `${API}/api/audit-response/${encodeURIComponent(token)}`;
  const [data, setData] = useState(initialData);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [area, setArea] = useState(initialArea);
  const [as, setAs] = useState(() => readAs(token));

  const load = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${api}/load`, { credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(d.message || 'This link could not be opened.');
      setData(d);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  // The first area shown: what the person chose last time on this link,
  // else the area their department suggests, else every area.
  useEffect(() => {
    if (!data || area) return;
    const areas = data.areas || [];
    const kept = readChoice(token);
    if (kept && (kept === ALL || areas.some((a) => a.key === kept))) { setArea(kept); return; }
    setArea(data.suggested_area && areas.some((a) => a.key === data.suggested_area.key) ? data.suggested_area.key : ALL);
  }, [data, area, token]);

  const choose = (k) => { setArea(k); keepChoice(token, k); };

  const shown = useMemo(() => (data ? data.listings.filter((l) => inArea(l, area || ALL)) : []), [data, area]);

  // Signed in, there is no gate: the page opens on its own.
  useEffect(() => { if (embedded && !data) load(); }, [embedded]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) {
    if (embedded) {
      return (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 text-[12.5px] text-slate-600">
          {err ? <span className="font-semibold text-rose-600">{err}</span> : 'Opening the audit…'}
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm max-w-md w-full p-6 text-center">
          <div className="text-[15px] font-bold text-slate-900">Audit report response</div>
          <p className="mt-2 text-[12.5px] text-slate-600">You were named on a draft internal audit report. Continue to read the findings and record your response to each one.</p>
          {err && <p className="mt-3 text-[12px] font-semibold text-rose-600">{err}</p>}
          <button onClick={load} disabled={busy} className="mt-4 px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold disabled:opacity-50">{busy ? 'Opening…' : 'Continue'}</button>
        </div>
      </div>
    );
  }

  const total = data.listings.length;
  const done = data.listings.filter(answered).length;
  const isPda = data.program === 'pda';
  const areas = data.areas || [];
  const hasAreas = !isPda && areas.length > 0;
  const current = area || ALL;
  const currentInfo = current === ALL ? null : areas.find((a) => a.key === current) || null;
  const shownDone = shown.filter(answered).length;
  const noun = isPda ? 'listing' : 'finding';
  const pct = total ? Math.round((done / total) * 100) : 0;
  const pending = !!data.report_pending;
  const programName = PROGRAM_NAMES[data.program] || String(data.program || '').toUpperCase();
  const shared = !!data.shared;
  const addressees = data.addressees || [];
  const asRow = shared ? addressees.find((a) => a.id === as) || null : null;
  const chooseAs = (id) => { setAs(id); keepAs(token, id); };

  // Signed in, before the draft report issues: the notice and the status,
  // nothing to respond to yet.
  if (pending) {
    return (
      <div className={embedded ? '' : 'min-h-screen bg-slate-50'}>
        <div className={embedded ? '' : 'max-w-5xl mx-auto p-4 sm:p-6'}>
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{data.org_name} Compliance · Audit and Monitoring</div>
            <div className="mt-1 text-[18px] font-bold text-slate-900">{programName} audit · {data.cycle_label}</div>
            <div className="mt-1 text-[12.5px] text-slate-600">
              You are named on this audit{data.notice?.sent_at ? <>; the engagement notice went out on <b>{fmt(data.notice.sent_at)}</b></> : null}. The draft report has not been issued yet. When Compliance issues it, the findings appear here with a response form for each one.
            </div>
          </div>
          {data.notice?.text && <NoticeBox text={data.notice.text} sentAt={data.notice.sent_at} open />}
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-slate-50'}>
      <div className={embedded ? '' : 'max-w-5xl mx-auto p-4 sm:p-6'}>
        {/* The report, the person, the clocks, the progress. */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{data.org_name} Compliance · Audit and Monitoring</div>
          <div className="mt-1 text-[18px] font-bold text-slate-900">Draft audit report · {programName} · {data.cycle_label}</div>
          <div className="mt-1 text-[12.5px] text-slate-600">
            {shared ? <>Sent to <b>{addressees.map((a) => a.name).join(', ')}</b>.</> : <>For <b>{data.recipient?.name || data.recipient?.email}</b>{data.recipient?.department ? <> ({data.recipient.department})</> : null}.</>}{' '}
            {data.closed ? 'The final report has been issued; responses are closed.' : <>Responses are due by <b>{fmt(data.response_due)}</b>; {isPda ? 'directory corrections' : 'corrective action plans'} by <b>{fmt(data.correction_due)}</b>.</>}
          </div>
          {data.notice?.text && <NoticeBox text={data.notice.text} sentAt={data.notice.sent_at} />}
          {shared && !data.closed && (
            <div className={`mt-3 rounded-xl px-4 py-3 ring-1 ${asRow ? 'bg-emerald-50/60 ring-emerald-200' : 'bg-amber-50 ring-amber-300'}`}>
              <label className="flex items-center gap-3 flex-wrap text-[12.5px] text-slate-700">
                <span className="font-semibold text-slate-800">I am responding as</span>
                <select value={as} onChange={(e) => chooseAs(e.target.value)} className="text-[12px] border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                  <option value="">Choose your name</option>
                  {addressees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <span className="text-[11.5px] text-slate-500">{asRow ? `Every response you save is recorded in the name of ${asRow.name}.` : 'This link was sent to everyone addressed on the draft report email. Choose your name before responding.'}</span>
              </label>
            </div>
          )}
          <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-3 items-center">
            <div>
              <div className="flex items-center justify-between text-[11.5px]">
                <span className={`font-semibold ${done === total ? 'text-emerald-700' : 'text-amber-700'}`}>{done} of {total} {noun}{total === 1 ? '' : 's'} responded</span>
                <span className="text-slate-400 tabular-nums">{pct}%</span>
              </div>
              <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${done === total ? 'bg-emerald-500' : 'bg-orange-500'}`} style={{ width: `${pct}%` }} /></div>
            </div>
            <details>
              <summary className="cursor-pointer text-[11.5px] font-semibold text-orange-700">Read the full draft report</summary>
              <div className="mt-2 whitespace-pre-wrap text-[11px] font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 max-h-96 overflow-y-auto sm:w-[36rem] max-w-full">{data.report_draft}</div>
            </details>
          </div>
          <ol className="mt-4 grid sm:grid-cols-3 gap-2 text-[11.5px] text-slate-600">
            <li className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2"><b className="text-slate-800">1. Pick your area.</b> {hasAreas ? 'Each finding is filed under the function its sample cases belong to. Choose yours, or all areas.' : 'Every finding on this report is listed below.'}</li>
            <li className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2"><b className="text-slate-800">2. Read the table.</b> One row per {noun}: what was found, how many sample cases, and whether you have responded.</li>
            <li className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2"><b className="text-slate-800">3. Respond to each one.</b> Agree, partially agree or disagree with the basis, then the correction, its owner and date, and attach evidence.</li>
          </ol>
        </div>

        {/* Your area, or all of them. */}
        {hasAreas && (
          <div className="mt-4 bg-white border border-slate-200 rounded-2xl shadow-sm p-4 sticky top-2 z-10">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Show findings for</div>
            <div className="flex items-center gap-2 flex-wrap">
              <AreaPill active={current === ALL} onClick={() => choose(ALL)} label="All areas" count={total} done={done} />
              {areas.map((a) => <AreaPill key={a.key} active={current === a.key} onClick={() => choose(a.key)} label={a.label} count={a.findings} done={a.responded} />)}
            </div>
            <div className="mt-2 text-[11.5px] text-slate-600">
              {currentInfo
                ? <><b className="text-slate-800">{currentInfo.label}</b> · {currentInfo.hint}. {shownDone} of {shown.length} responded.</>
                : <>Every finding on the report, grouped by area. {areas.map((a) => `${a.label}: ${a.findings}`).join(' · ')}.</>}
              {data.suggested_area && current === data.suggested_area.key && data.recipient?.department && (
                <span className="text-slate-400"> Opened on this area because your department is {data.recipient.department}; choose All areas to see everything.</span>
              )}
            </div>
          </div>
        )}

        {err && <div className="mt-3 text-[12px] font-semibold text-rose-600">{err}</div>}

        {/* The findings at a glance: one row each. */}
        {shown.length > 0 && (
          <div className="mt-4 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-4 pt-3 pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">{isPda ? 'Deficient listings' : 'Findings'}{currentInfo ? ` · ${currentInfo.label}` : ' · all areas'} ({shown.length})</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left font-semibold px-4 py-2 w-8">#</th>
                    {isPda ? (<>
                      <th className="text-left font-semibold px-3 py-2">Provider</th>
                      <th className="text-left font-semibold px-3 py-2">NPI</th>
                      <th className="text-left font-semibold px-3 py-2">What the audit found</th>
                    </>) : (<>
                      <th className="text-left font-semibold px-3 py-2">Finding</th>
                      {current === ALL && <th className="text-left font-semibold px-3 py-2">Area</th>}
                      <th className="text-left font-semibold px-3 py-2">Classification</th>
                      <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">{data.program === 'website' ? 'Rows affected' : 'Sample cases'}</th>
                      <th className="text-left font-semibold px-3 py-2">Universe</th>
                    </>)}
                    <th className="text-left font-semibold px-3 py-2">Status</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((l, i) => (
                    <tr key={l.id || l.caseId} className="border-t border-slate-100 align-top hover:bg-orange-50/40">
                      <td className="px-4 py-2 text-slate-400 tabular-nums">{i + 1}</td>
                      {isPda ? (<>
                        <td className="px-3 py-2 font-semibold text-slate-800">{l.provider || l.title || 'Provider'}</td>
                        <td className="px-3 py-2 text-slate-600 tabular-nums">{l.ref}</td>
                        <td className="px-3 py-2 text-slate-700">{l.finding}</td>
                      </>) : (<>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-800">{l.title}</div>
                          <div className="text-slate-500">{l.statement?.short || l.subtitle || l.requirement}</div>
                          {l.cases?.some((k) => k.comments) ? <div className="text-slate-400">{l.cases.filter((k) => k.comments).length} auditor note{l.cases.filter((k) => k.comments).length === 1 ? '' : 's'}</div> : null}
                        </td>
                        {current === ALL && <td className="px-3 py-2 text-slate-600">{(l.areas || []).map((k) => (areas.find((a) => a.key === k) || {}).label || k).join(', ')}</td>}
                        <td className="px-3 py-2"><ClassChip finding={l.finding} classification={l.classification} /></td>
                        <td className="px-3 py-2 text-slate-700 tabular-nums whitespace-nowrap">{l.cases?.length ? `${l.cases.length} of ${l.reviewed || l.cases.length}` : l.universe ? 'universe-level' : '—'}</td>
                        <td className="px-3 py-2 text-slate-600">{universesOf(l)}</td>
                      </>)}
                      <td className="px-3 py-2"><StatusChip done={answered(l)} dispositioned={!!l.response?.dispositioned} /></td>
                      <td className="px-3 py-2 text-right whitespace-nowrap"><a href={`#finding-${anchorOf(l)}`} className="text-[11.5px] font-semibold text-orange-700 hover:underline">{answered(l) ? 'Review' : 'Respond'} →</a></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* The findings in full, grouped by area, each with its response. */}
        <div className="mt-4 space-y-3">
          {groupByArea(shown, current, areas).map((g) => (
            <React.Fragment key={g.key}>
              {g.label && (
                <div className="pt-2 flex items-baseline gap-2 flex-wrap">
                  <div className="text-[13px] font-bold text-slate-800">{g.label}</div>
                  <div className="text-[11px] text-slate-500">{g.hint} · {g.items.length} {noun}{g.items.length === 1 ? '' : 's'}</div>
                </div>
              )}
              {g.items.map((l) => (
                <ListingCard key={l.id || l.caseId} l={l} api={api} closed={data.closed} onSaved={load} setErr={setErr} areas={areas} website={data.program === 'website'} as={as} needAs={shared} />
              ))}
            </React.Fragment>
          ))}
          {!total && <div className="text-[12px] text-slate-500 bg-white border border-slate-200 rounded-2xl p-5">No {isPda ? 'deficient listings require' : 'conditions require'} a response.</div>}
          {total > 0 && !shown.length && <div className="text-[12px] text-slate-500 bg-white border border-slate-200 rounded-2xl p-5">No findings in this area. Choose All areas to see the whole report.</div>}
        </div>
        <p className="mt-6 text-[10.5px] text-slate-400">{embedded ? <>You are signed in as {data.recipient?.name || data.recipient?.email}.</> : shared ? 'This link was sent to everyone addressed on the draft report email; do not forward it.' : 'This link is personal to you.'} Every response is recorded with your name, the date and time. The audit team decides whether a disagreement is accepted; the final report states the disposition of every response.</p>
      </div>
    </div>
  );
}

/** The engagement notice as it went out, folded under the report header. */
function NoticeBox({ text, sentAt, open = false }) {
  return (
    <details className="mt-3" open={open}>
      <summary className="cursor-pointer text-[11.5px] font-semibold text-orange-700">Read the engagement notice{sentAt ? ` (sent ${fmt(sentAt)})` : ''}</summary>
      <div className="mt-2 whitespace-pre-wrap text-[11px] font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 max-h-96 overflow-y-auto">{text}</div>
    </details>
  );
}

const anchorOf = (l) => encodeURIComponent(String(l.id || l.caseId || '')).replace(/%/g, '_');
const universesOf = (l) => {
  const names = [...new Set((l.cases || []).map((k) => k.universeLabel || k.universe).filter(Boolean))];
  if (names.length) return names.join(', ');
  return l.universeLabel || l.universe || '—';
};

// Cards grouped by area: by the selected area alone, or by each finding's
// primary area when every area shows.
function groupByArea(list, current, areas) {
  if (current !== ALL || !areas.length) return [{ key: current, label: null, items: list }];
  const groups = [];
  for (const a of areas) {
    const items = list.filter((l) => primaryArea(l) === a.key);
    if (items.length) groups.push({ key: a.key, label: a.label, hint: a.hint, items });
  }
  const rest = list.filter((l) => !areas.some((a) => a.key === primaryArea(l)));
  if (rest.length) groups.push({ key: 'rest', label: 'Program-wide', hint: 'Not tied to one universe table', items: rest });
  return groups;
}

function AreaPill({ active, onClick, label, count, done }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-[12px] font-semibold ring-1 transition ${active ? 'bg-orange-500 text-white ring-orange-500' : 'bg-white text-slate-600 ring-slate-200 hover:ring-orange-300 hover:text-slate-900'}`}>
      {label} <span className={`ml-1 tabular-nums ${active ? 'text-orange-100' : 'text-slate-400'}`}>{done}/{count}</span>
    </button>
  );
}

function ClassChip({ finding, classification }) {
  const cls = classification === 'observation' ? 'bg-sky-50 text-sky-700 ring-sky-200'
    : classification === 'ids' ? 'bg-violet-50 text-violet-700 ring-violet-200'
      : classification === 'icar' ? 'bg-rose-50 text-rose-700 ring-rose-200'
        : 'bg-amber-50 text-amber-800 ring-amber-200';
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-bold ring-1 whitespace-nowrap ${cls}`}>{finding}</span>;
}

function StatusChip({ done, dispositioned }) {
  if (dispositioned) return <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-slate-100 text-slate-600 whitespace-nowrap">Dispositioned</span>;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-bold whitespace-nowrap ${done ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{done ? 'Responded' : 'Response needed'}</span>;
}

// The endpoint path for a finding: a listing responds per case, a condition
// per condition key.
const pathOf = (l) => (l.kind === 'condition' ? `conditions/${encodeURIComponent(l.key)}` : `listings/${l.caseId}`);

/** The sampled record as submitted in the universe, so the department can find the case. */
function RecordGrid({ record, title }) {
  if (!record || typeof record !== 'object') return null;
  const entries = Array.isArray(record._columns)
    ? record._columns.filter((k) => k in record && !k.startsWith('_')).map((k) => [k, record[k]])
    : Object.entries(record).filter(([k]) => !k.startsWith('_'));
  return (
    <div className="mt-2 rounded-xl bg-sky-50/60 ring-1 ring-sky-200 px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-sky-800 mb-1.5">{title}</p>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        {entries.map(([label, value]) => (
          <div key={label} className="flex items-baseline gap-2 text-[12px] min-w-0">
            <span className="text-slate-500 flex-none max-w-[60%] break-words">{label}:</span>
            {String(value ?? '').trim()
              ? <span className="text-slate-800 font-medium min-w-0 break-words">{String(value)}</span>
              : <span className="text-rose-600 font-medium italic">blank</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ListingCard({ l, api, closed, onSaved, setErr, areas = [], website = false, as = '', needAs = false }) {
  // On a shared link every write names the person picked at the top.
  const q = needAs && as ? `?as=${encodeURIComponent(as)}` : '';
  const r = l.response || {};
  const locked = closed || !!r.dispositioned;
  const isCondition = l.kind === 'condition';
  const [position, setPosition] = useState(r.position || '');
  const [basis, setBasis] = useState(r.basis || '');
  const [plan, setPlan] = useState(r.plan || '');
  const [owner, setOwner] = useState(r.owner || '');
  const [due, setDue] = useState(r.targetDate ? String(r.targetDate).slice(0, 10) : '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(!!r.position);
  const needPlan = position && position !== 'disagree';
  const canSave = !locked && (!needAs || !!as) && position && (position === 'agree' || basis.trim().length >= 10) && (!needPlan || (plan.trim() && owner.trim() && due));
  const areaLabels = (l.areas || []).map((k) => (areas.find((a) => a.key === k) || {}).label || null).filter(Boolean);
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${api}/${pathOf(l)}/respond${q}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ position, basis, correction_plan: plan, owner_name: owner, target_date: due }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.message || 'Could not save.');
      setSaved(true); onSaved();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const Choice = ({ k, label, cls }) => (
    <button type="button" disabled={locked} onClick={() => { setPosition(k); setSaved(false); }}
      className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold ring-1 ${position === k ? cls : 'bg-white text-slate-500 ring-slate-200 hover:text-slate-700'} disabled:opacity-60`}>{label}</button>
  );
  return (
    <div id={`finding-${anchorOf(l)}`} className="bg-white border border-slate-200 rounded-2xl p-5 scroll-mt-28">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[14rem]">
          <div className="text-[13px] font-bold text-slate-900">{l.title || l.provider || 'Finding'} <span className="text-slate-400 font-normal">· {l.subtitle || `NPI ${l.ref}`}</span></div>
          <div className="text-[11.5px] text-slate-500">
            {isCondition && areaLabels.length ? <span className="font-semibold text-slate-600">{areaLabels.join(' · ')}</span> : null}
            {isCondition && areaLabels.length && l.context ? ' · ' : ''}
            {l.context}
          </div>
        </div>
        {isCondition && <ClassChip finding={l.finding} classification={l.classification} />}
        <StatusChip done={saved} dispositioned={!!r.dispositioned} />
      </div>
      {Array.isArray(l.directory) && l.directory.length > 0 && (
        <div className="mt-3 rounded-xl bg-sky-50/60 ring-1 ring-sky-200 px-4 py-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-sky-800 mb-1.5">What the directory says for this listing</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
            {l.directory.map(([label, value]) => (
              <div key={label} className="flex items-baseline gap-2 text-[12px] min-w-0">
                <span className="text-slate-500 flex-none max-w-[60%] break-words">{label}:</span>
                {String(value ?? '').trim()
                  ? <span className="text-slate-800 font-medium min-w-0 break-words">{String(value)}</span>
                  : <span className="text-rose-600 font-medium italic">blank</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {!isCondition && <RecordGrid record={l.record} title="The sampled record, as submitted in the universe" />}
      <div className="mt-3 rounded-xl bg-rose-50/60 ring-1 ring-rose-100 px-4 py-3 text-[12px] text-slate-700 space-y-1">
        {isCondition ? (<>
          <div><span className="font-semibold text-rose-700">Classification:</span> {l.finding}.</div>
          {l.cases?.length && l.statement?.lead
            ? <div><span className="font-semibold">Condition:</span> {l.statement.lead}{l.statement.met ? ` ${l.statement.met}` : ''}</div>
            : <div><span className="font-semibold">Condition:</span> {l.cases?.length ? `In ${l.cases.length} of ${l.reviewed} sampled cases reviewed in ${l.context}, the requirement was not met: ` : ''}{l.requirement}{l.requirement && !/[.]$/.test(l.requirement) ? '.' : ''}</div>}
          {l.criteria?.length > 0 && <div><span className="font-semibold">Criteria:</span> {l.criteria.join('; ')}.</div>}
          {l.cause && <div><span className="font-semibold">Cause:</span> {l.cause}</div>}
          {l.effect && <div><span className="font-semibold">Effect:</span> {l.effect}</div>}
          {l.universe && !l.cases?.length && <div><span className="font-semibold">Universe:</span> {l.universeLabel || l.universe}</div>}
          {l.cases?.some((k) => k.comments) && (
            <div>
              <span className="font-semibold">{website ? 'Auditor\'s notes on the rows:' : 'Auditor\'s notes on the sample cases:'}</span>
              <ul className="mt-0.5 ml-4 list-disc space-y-0.5">
                {l.cases.filter((k) => k.comments).map((k) => <li key={`note-${k.caseId}`}><b>{k.ref}</b>{k.universeLabel || k.universe ? ` (${k.universeLabel || k.universe})` : ''}: {k.comments}</li>)}
              </ul>
            </div>
          )}
          {l.requiredAction && (
            <div className="mt-1.5 pt-1.5 border-t border-rose-200/70">
              <span className="font-semibold text-rose-700">Corrective action required:</span> {l.requiredAction}{/[.!?]$/.test(l.requiredAction) ? '' : '.'}
              {l.classification !== 'observation' && <span className="text-slate-500"> Your response below states the planned correction, its owner and date.</span>}
            </div>
          )}
        </>) : (<>
          <div><span className="font-semibold text-rose-700">What the audit found:</span> {l.finding || (l.deficiency ? `${l.deficiency.label} (CMS weight ${l.deficiency.weight})` : 'a condition')}{l.deficiencies && l.deficiencies.length > 1 ? `; also ${l.deficiencies.filter((d) => d.label !== l.deficiency?.label).map((d) => d.label).join('; ')}` : ''}.</div>
          {l.unverified ? <div>The office could not be reached after three attempts{l.attempts?.length ? ` (${l.attempts.map((a) => `${a.on || ''} ${a.at || ''} ${a.result || ''}`.trim()).join('; ')})` : ''}.</div>
            : l.failedChecks?.length ? <div><span className="font-semibold">Checks that failed:</span> {l.failedChecks.join(' | ')}</div> : null}
          {l.comments && <div><span className="font-semibold">Auditor's note:</span> {l.comments}</div>}
          {l.correction && <div className="text-emerald-800"><span className="font-semibold">Correction recorded:</span> {l.correction}</div>}
        </>)}
      </div>
      {isCondition && l.cases?.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[11.5px] font-semibold text-orange-700">{website ? `Rows under this finding (${l.statement?.short || l.cases.length})` : `Sample cases under this condition (${l.cases.length})`}</summary>
          <div className="mt-1 overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left font-semibold px-3 py-1.5">Case</th>
                  <th className="text-left font-semibold px-3 py-1.5">Universe</th>
                  <th className="text-left font-semibold px-3 py-1.5">Area</th>
                  <th className="text-left font-semibold px-3 py-1.5">Service or issue</th>
                  <th className="text-left font-semibold px-3 py-1.5">Auditor's note</th>
                </tr>
              </thead>
              <tbody>
                {l.cases.map((k) => (
                  <tr key={k.caseId} className="border-t border-slate-100 align-top">
                    <td className="px-3 py-1.5 font-semibold text-slate-800 whitespace-nowrap">{k.ref}</td>
                    <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{k.universeLabel || k.universe || '—'}</td>
                    <td className="px-3 py-1.5 text-slate-600">{(areas.find((a) => a.key === k.area) || {}).label || '—'}</td>
                    <td className="px-3 py-1.5 text-slate-600">{k.context || '—'}</td>
                    <td className="px-3 py-1.5 text-slate-600">{k.comments || '—'}{k.correction ? <div className="text-emerald-800">Correction recorded: {k.correction}</div> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-1 space-y-1.5">
            {l.cases.filter((k) => k.record).map((k) => (
              <div key={`rec-${k.caseId}`} className="rounded-xl border border-slate-200 px-3.5 py-2 text-[12px] text-slate-700">
                <div><b>{k.ref}</b>{k.universeLabel || k.universe ? ` · ${k.universeLabel || k.universe}` : ''}{k.context ? ` · ${k.context}` : ''}</div>
                <RecordGrid record={k.record} title="The sampled record, as submitted in the universe" />
              </div>
            ))}
          </div>
        </details>
      )}
      <div className="mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Your response</div>
        <div className="flex items-center gap-2 flex-wrap">
          <Choice k="agree" label="Agree" cls="bg-emerald-50 text-emerald-700 ring-emerald-200" />
          <Choice k="partially_agree" label="Partially agree" cls="bg-amber-50 text-amber-700 ring-amber-200" />
          <Choice k="disagree" label="Disagree" cls="bg-rose-50 text-rose-700 ring-rose-200" />
        </div>
        {position && position !== 'agree' && (
          <textarea value={basis} onChange={(e) => { setBasis(e.target.value); setSaved(false); }} disabled={locked} rows={3}
            placeholder={position === 'disagree' ? 'The basis for the disagreement, with the evidence (required)' : 'What you agree with and what you do not, with the evidence'}
            className="mt-2 w-full text-[12px] border border-slate-300 rounded-xl px-3 py-2 disabled:bg-slate-50" />
        )}
        {needPlan && (
          <div className="mt-2 grid sm:grid-cols-[1fr_12rem_9rem] gap-2">
            <input value={plan} onChange={(e) => { setPlan(e.target.value); setSaved(false); }} disabled={locked} placeholder={isCondition ? 'Planned corrective action' : 'Planned correction to the directory'}
              className="text-[12px] border border-slate-300 rounded-xl px-3 py-2 disabled:bg-slate-50" />
            <input value={owner} onChange={(e) => { setOwner(e.target.value); setSaved(false); }} disabled={locked} placeholder="Owner (name)"
              className="text-[12px] border border-slate-300 rounded-xl px-3 py-2 disabled:bg-slate-50" />
            <input type="date" value={due} onChange={(e) => { setDue(e.target.value); setSaved(false); }} disabled={locked}
              className="text-[12px] border border-slate-300 rounded-xl px-3 py-2 disabled:bg-slate-50" />
          </div>
        )}
        {!locked && (
          <div className="mt-2 flex items-center gap-3">
            <button onClick={save} disabled={!canSave || busy} className="px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-[12px] font-semibold disabled:opacity-50">{busy ? 'Saving…' : saved ? 'Update response' : 'Save response'}</button>
            {r.submittedAt && <span className="text-[11px] text-slate-400">Last saved {new Date(r.submittedAt).toLocaleString('en-US')}</span>}
          </div>
        )}
        <EvidenceBox l={l} api={api} locked={locked || (needAs && !as)} onChanged={onSaved} setErr={setErr} q={q} />
        {r.dispositioned && <div className="mt-2 text-[11px] text-slate-500">The audit team has recorded its disposition of this response; it appears in the final report.</div>}
      </div>
    </div>
  );
}

/** Remediation evidence: Word, PDF or screenshots attached to this listing. */
function EvidenceBox({ l, api, locked, onChanged, setErr, q = '' }) {
  const [busy, setBusy] = useState(false);
  const files = l.evidence || [];
  const upload = async (list) => {
    if (!list || !list.length) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); for (const f of list) fd.append('files', f);
      const res = await fetch(`${api}/${pathOf(l)}/evidence${q}`, { method: 'POST', credentials: 'include', body: fd });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.message || 'Could not attach the file.');
      onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const remove = async (id) => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${api}/${pathOf(l)}/evidence/${id}`, { method: 'DELETE', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.message || 'Could not remove the file.');
      onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div className="mt-3 rounded-xl border border-dashed border-slate-300 px-3.5 py-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Evidence of remediation</span>
        <span className="text-[10.5px] text-slate-400">Word, PDF or screenshots (PNG, JPG). Up to 10 MB each.</span>
        {!locked && (
          <label className={`ml-auto px-3 py-1.5 rounded-lg border border-orange-300 text-orange-700 hover:border-orange-400 text-[11.5px] font-semibold cursor-pointer ${busy ? 'opacity-50' : ''}`}>
            {busy ? 'Attaching…' : '+ Attach files'}
            <input type="file" multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp" className="hidden" disabled={busy} onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ''; }} />
          </label>
        )}
      </div>
      {files.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {files.map((f) => (
            <li key={f.id} className="flex items-center gap-2 text-[11.5px] text-slate-700 bg-slate-50 rounded-lg px-2.5 py-1.5">
              <span>📎</span><span className="truncate flex-1">{f.file_name}</span>
              <span className="text-slate-400 tabular-nums">{Math.max(1, Math.round((f.size || 0) / 1024))} KB</span>
              {!locked && <button type="button" onClick={() => remove(f.id)} className="text-[10.5px] font-semibold text-slate-400 hover:text-rose-600">Remove</button>}
            </li>
          ))}
        </ul>
      )}
      {!files.length && <div className="mt-1 text-[11px] text-slate-400">Nothing attached yet.</div>}
    </div>
  );
}
